"use client";

import React, { useState } from "react";
import { ArrowRight, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
export default function RelatedLinksAndEstimate() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    project_type: "",
    details: "",
    contact_method: "",
    timing: "",
    budget: "",
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const relatedLinks = [{
    title: "Remodeling Cost Guide",
    description: "Explore neighborhood pricing estimates and renovation investment ranges.",
    to: "/remodeling-cost-guide"
  }, {
    title: "Process & Planning",
    description: "Learn how we guide your renovation from first concept through final walkthrough.",
    to: "/process-planning"
  }, {
    title: "FAQ",
    description: "Get quick answers to common questions about permits and timelines.",
    to: "/faq"
  }];
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.consent) {
      setErrorMessage("Please agree to the contact consent to proceed.");
      return;
    }
    setIsSubmitting(true);
    setErrorMessage(null);
    const currentPage = typeof window !== "undefined" ? window.location.pathname : "";
    const params = new URLSearchParams();
    params.append("name", formData.name);
    params.append("phone", formData.phone);
    params.append("email", formData.email);
    params.append("address", formData.address);
    params.append("project_type", formData.project_type);
    params.append("details", formData.details);
    params.append("contact_method", formData.contact_method);
    params.append("timing", formData.timing);
    params.append("budget", formData.budget);
    params.append("consent", formData.consent ? "true" : "false");
    params.append("website", "DecaturHomeRenovation.com");
    params.append("page", currentPage);
    params.append("lead_source", "organic");
    try {
      const response = await fetch("https://hooks.zapier.com/hooks/catch/28361868/4mcz4d7/", {
        method: "POST",
        body: params
      });
      if (response.ok) {
        setIsSuccess(true);
      } else {
        setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
      }
    } catch {
      setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="relatedlinksandestimate" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-t border-border">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Related Links Grid */}
        <div className="mb-16 sm:mb-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedLinks.map((link, i) => <Card key={link.to} data-index={i} className="group border border-border/80 bg-card/60 backdrop-blur-xs hover:border-foreground/30 transition-all duration-200 shadow-xs hover:shadow-md rounded-xl">
                <CardContent className="p-6 flex flex-col justify-between h-full">
                  <div>
                    <h3 className="font-serif text-xl font-medium tracking-tight text-foreground group-hover:text-chart-1 transition-colors">
                      {link.title}
                    </h3>
                    <p className="font-sans text-sm text-muted-foreground mt-2 leading-relaxed">
                      {link.description}
                    </p>
                  </div>
                  <div className="mt-6 pt-4 border-t border-border/40">
                    <Link to={link.to} className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-foreground hover:text-chart-1 transition-colors">
                      <span>Explore details</span>
                      <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {/* Estimate Form Section */}
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-serif text-3xl sm:text-4xl font-normal tracking-tight text-foreground">
              Request a Project Estimate
            </h2>
            <p className="font-sans text-sm sm:text-base text-muted-foreground mt-3 max-w-xl mx-auto">
              Share your renovation plans and timeline. We will review your project details and follow up promptly.
            </p>
          </div>

          <Card className="border border-border/80 bg-card/80 shadow-sm rounded-xl overflow-hidden">
            <CardContent className="p-6 sm:p-10">
              {isSuccess ? <div className="text-center py-12 px-4 space-y-4">
                  <div className="mx-auto size-12 rounded-full bg-chart-1/10 flex items-center justify-center text-chart-1">
                    <CheckCircle2 className="size-6" />
                  </div>
                  <h3 className="font-serif text-2xl font-normal text-foreground">
                    Estimate Request Received
                  </h3>
                  <p className="font-sans text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
                    Thank you for reaching out. Our remodeling team will review your specifications and contact you via your preferred communication method.
                  </p>
                </div> : <form onSubmit={handleSubmit} className="space-y-6">
                  {errorMessage && <Alert variant="destructive" className="rounded-lg">
                      <AlertCircle className="size-4" />
                      <AlertTitle>Submission Error</AlertTitle>
                      <AlertDescription>{errorMessage}</AlertDescription>
                    </Alert>}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Name */}
                    <div className="space-y-2">
                      <Label htmlFor="estimate-name" className="text-foreground">
                        Name <span className="text-destructive">*</span>
                      </Label>
                      <Input id="estimate-name" type="text" required placeholder="Full Name" value={formData.name} onChange={e => setFormData({
                    ...formData,
                    name: e.target.value
                  })} className="bg-background" />
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                      <Label htmlFor="estimate-phone" className="text-foreground">
                        Phone <span className="text-destructive">*</span>
                      </Label>
                      <Input id="estimate-phone" type="tel" required placeholder="Your phone number" value={formData.phone} onChange={e => setFormData({
                    ...formData,
                    phone: e.target.value
                  })} className="bg-background" />
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="estimate-email" className="text-foreground">
                        Email <span className="text-destructive">*</span>
                      </Label>
                      <Input id="estimate-email" type="email" required placeholder="Your email address" value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} className="bg-background" />
                    </div>

                    {/* Address / Zip */}
                    <div className="space-y-2">
                      <Label htmlFor="estimate-address" className="text-foreground">
                        Street Address or ZIP Code <span className="text-destructive">*</span>
                      </Label>
                      <Input id="estimate-address" type="text" required placeholder="e.g. 30030 or Street Address" value={formData.address} onChange={e => setFormData({
                    ...formData,
                    address: e.target.value
                  })} className="bg-background" />
                    </div>

                    {/* Project Type */}
                    <div className="space-y-2">
                      <Label htmlFor="estimate-project-type" className="text-foreground">
                        Project Type <span className="text-destructive">*</span>
                      </Label>
                      <Select required value={formData.project_type} onValueChange={val => setFormData({
                    ...formData,
                    project_type: val
                  })}>
                        <SelectTrigger id="estimate-project-type" className="w-full bg-background">
                          <SelectValue placeholder="Select project type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Kitchen Remodeling">Kitchen Remodeling</SelectItem>
                          <SelectItem value="Bathroom Remodeling">Bathroom Remodeling</SelectItem>
                          <SelectItem value="Whole-Home Remodeling">Whole-Home Remodeling</SelectItem>
                          <SelectItem value="Home Additions">Home Additions</SelectItem>
                          <SelectItem value="Basement Remodeling">Basement Remodeling</SelectItem>
                          <SelectItem value="Interior Remodeling">Interior Remodeling</SelectItem>
                          <SelectItem value="Outdoor Living">Outdoor Living</SelectItem>
                          <SelectItem value="Deck Remodeling">Deck Remodeling</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Contact Method */}
                    <div className="space-y-2">
                      <Label htmlFor="estimate-contact-method" className="text-foreground">
                        Preferred Contact Method <span className="text-destructive">*</span>
                      </Label>
                      <Select required value={formData.contact_method} onValueChange={val => setFormData({
                    ...formData,
                    contact_method: val
                  })}>
                        <SelectTrigger id="estimate-contact-method" className="w-full bg-background">
                          <SelectValue placeholder="Select contact method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Phone">Phone</SelectItem>
                          <SelectItem value="Text">Text</SelectItem>
                          <SelectItem value="Email">Email</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Timing */}
                    <div className="space-y-2">
                      <Label htmlFor="estimate-timing" className="text-foreground">
                        Project Timing <span className="text-destructive">*</span>
                      </Label>
                      <Select required value={formData.timing} onValueChange={val => setFormData({
                    ...formData,
                    timing: val
                  })}>
                        <SelectTrigger id="estimate-timing" className="w-full bg-background">
                          <SelectValue placeholder="Select project timing" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ASAP">ASAP</SelectItem>
                          <SelectItem value="Within 1 month">Within 1 month</SelectItem>
                          <SelectItem value="1-3 months">1-3 months</SelectItem>
                          <SelectItem value="3-6 months">3-6 months</SelectItem>
                          <SelectItem value="Just researching">Just researching</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Budget */}
                    <div className="space-y-2">
                      <Label htmlFor="estimate-budget" className="text-foreground">
                        Estimated Budget
                      </Label>
                      <Select value={formData.budget} onValueChange={val => setFormData({
                    ...formData,
                    budget: val
                  })}>
                        <SelectTrigger id="estimate-budget" className="w-full bg-background">
                          <SelectValue placeholder="Select budget range" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Under $25,000">Under $25,000</SelectItem>
                          <SelectItem value="$25,000-$50,000">$25,000-$50,000</SelectItem>
                          <SelectItem value="$50,000-$100,000">$50,000-$100,000</SelectItem>
                          <SelectItem value="$100,000+">$100,000+</SelectItem>
                          <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="space-y-2">
                    <Label htmlFor="estimate-details" className="text-foreground">
                      Project Details
                    </Label>
                    <Textarea id="estimate-details" rows={4} placeholder="Tell us about the scope, specific layout changes, materials, or goals for your project..." value={formData.details} onChange={e => setFormData({
                  ...formData,
                  details: e.target.value
                })} className="bg-background" />
                  </div>

                  {/* Consent */}
                  <div className="pt-2">
                    <div className="flex items-start gap-3">
                      <Checkbox id="estimate-consent" required checked={formData.consent} onCheckedChange={checked => setFormData({
                    ...formData,
                    consent: checked === true
                  })} />
                      <Label htmlFor="estimate-consent" className="text-xs sm:text-sm font-normal text-muted-foreground leading-snug cursor-pointer select-none">
                        I agree to be contacted about my project by phone, text, or email.
                      </Label>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-4">
                    <Button type="submit" disabled={isSubmitting} className="w-full h-11 text-base font-medium rounded-md transition-all shadow-xs">
                      {isSubmitting ? <>
                          <Loader2 className="size-4 animate-spin mr-2" />
                          <span>Sending Request...</span>
                        </> : "Submit Estimate Request"}
                    </Button>
                  </div>
                </form>}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}